import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
    IconButton,
    TextField
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
    forwardRef,
    useImperativeHandle,
    useMemo,
    useState
} from "react";
import { connect } from "react-redux";
import AutocompleteCompo from "../../components/Comman/AutoCompleteCompoForScheme";
import { blueFadeColor, lightWhiteFadeColor, white } from "../../config/ColorObj";
import { areYouSureMsg, deleteWarningMsg } from "../../config/messageconstant";
import { showLoador } from "../Landing/Landing.slice";
import "./Scheme.css";

/* ---------- Custom Inputs ---------- */

const CustomTextField = styled(TextField)(({ theme }) => ({
    "& .MuiOutlinedInput-root": {
        height: 42,
        borderRadius: "10px",

        "& fieldset": {
            borderColor: theme.palette.grey[300],
        },
        "&:hover fieldset": {
            borderColor: blueFadeColor,
        },
        "&.Mui-focused fieldset": {
            borderColor: blueFadeColor,
            borderWidth: 1.5,
        },
    },
    "& .MuiOutlinedInput-input": {
        padding: "8px 10px",
        fontSize: "14px",
    },
}));


const SchemeProductList = forwardRef((props, ref) => {

    const { productList, offerLevelDetailsData } = props;
    const [rows, setRows] = useState([]);
    const [errors, setErrors] = useState({
        product: "",
        percentage: "",
        qty: "",
    });

    const [shake, setShake] = useState({
        product: false,
        percentage: false,
        qty: false,
    });

    const [selection, setSelection] = useState({
        product: "",
        percentage: "",
        qty: "",
    });

    /* ------------------ COMMON AUTOCOMPLETE ------------------ */
    const commonAutoProps = {
        size: "small",
        getOptionLabel: (o) => o?.name || "",
        isOptionEqualToValue: (o, v) => o?.id === v?.id,

        sx: {
            "& .MuiInputBase-input": {
                fontSize: "14px",
                fontFamily: "Poppins, Helvetica, Arial, sans-serif",
            },
        },

        componentsProps: {
            paper: {
                sx: {
                    fontSize: "14px",
                    fontFamily: "Poppins, Helvetica, Arial, sans-serif",
                },
            },
            popper: {
                sx: {
                    "& .MuiAutocomplete-option": {
                        fontSize: "14px",
                        fontFamily: "Poppins, Helvetica, Arial, sans-serif",
                        padding: "8px 12px",
                        borderRadius: "6px",
                        margin: "2px 6px",

                        "&.Mui-selected": {
                            backgroundColor: "#4861df !important",
                            color: white,
                        },
                        "&.Mui-selected.Mui-focused": {
                            backgroundColor: "#4861df !important",
                        },
                        "&:hover": {
                            backgroundColor: lightWhiteFadeColor,
                        },
                    },
                },
            },
        },
    };


    const productOptions = useMemo(() => {
        // Check if level type requires activeProduct

        if (
            offerLevelDetailsData?.levelType?.id == "3" ||
            offerLevelDetailsData?.levelType?.id == "6" ||
            offerLevelDetailsData?.levelType?.id == "7"
        ) {

            return Array.isArray(productList?.activeProduct)
                ? productList.activeProduct
                : [];
        }

        // Default case
        return Array.isArray(productList?.activeProductByCategory)
            ? productList.activeProductByCategory
            : [];
    }, [
        offerLevelDetailsData?.levelType?.id,
        productList?.activeProduct,
        productList?.activeProductByCategory,
    ]);


    const handleAdd = () => {
        const { isValid } = validate();
        if (!isValid) return;

        let product = {};

        if (
            offerLevelDetailsData?.levelType?.id == "3" ||
            offerLevelDetailsData?.levelType?.id == "6" ||
            offerLevelDetailsData?.levelType?.id == "7"
        ) {
            product = productList.activeProduct.find(
                (row) => row.id == selection.product.id
            );
        } else {
            product = productList?.activeProductByCategory.find(
                (row) => row.id == selection.product.id
            );
        }

        const objectToSave = {
            id: Date.now(),
            product: product?.name || "",
            qty: selection?.qty || "-",
            percentage: selection?.percentage || "-",
            productId: product?.id || "",
        };

        setRows((prev) => [...prev, objectToSave]);
        setSelection({ product: "", qty: "", percentage: "" });
    };

    const validate = () => {
        let valid = true;
        const newErrors = {};
        const newShake = {};

        const isPercentageRequired = offerLevelDetailsData?.levelType?.id === 4;

        const rules = {
            product: !selection.product,
            percentage: isPercentageRequired && !selection.percentage,
            qty: !isPercentageRequired && !selection.qty,
        };

        Object.keys(rules).forEach((key) => {
            if (rules[key]) {
                valid = false;

                newErrors[key] = `${key
                    .replace(/([A-Z])/g, " $1")
                    .replace(/^./, (str) => str.toUpperCase())} is required`;

                newShake[key] = true;

                setTimeout(() => {
                    setShake((prev) => ({ ...prev, [key]: false }));
                }, 400);
            }
        });

        setErrors(newErrors);
        setShake(newShake);

        return { isValid: valid, data: selection };
    };

    /* ---------- CHANGE HANDLER ---------- */
    const handleChange = (key, value) => {
        setSelection((prev) => ({ ...prev, [key]: value }));
        setErrors((prev) => ({ ...prev, [key]: "" }));
        setShake((prev) => ({ ...prev, [key]: false }));
    };

    const rowDelete = (rowData) => {
        swal({
            title: areYouSureMsg,
            text: deleteWarningMsg,
            icon: "warning",
            buttons: true,
            dangerMode: true,
        }).then((willDelete) => {
            if (willDelete) {
                const updatedRows = rows.filter(
                    (row) => row.id !== rowData.id
                );
                setRows(updatedRows);
            }
        });
    };


    /* ---------- EXPOSE TO PARENT ---------- */
    useImperativeHandle(ref, () => ({
        validate,
        getData: () => rows,
    }));

    return (
        <div className="card-scheme">
            <div className="card-title-scheme">🧾 Product Details</div>

            {/* ---------- HEADER INPUTS ---------- */}

            <div className="grid-scheme">
                <div className={shake.product ? "shake-error" : ""}>
                    <label>Product</label>
                    <AutocompleteCompo
                        {...commonAutoProps}
                        size="small"
                        options={productOptions}
                        value={selection.product}
                        onChange={(e, val) => handleChange("product", val)}
                        getOptionLabel={(option) =>
                            option?.productName || option?.name || ""
                        }
                        renderInput={(params) => (
                            <CustomTextField
                                {...params}
                                placeholder="Select Product"
                                error={Boolean(errors.product)}
                                helperText={errors.product}
                            />
                        )}
                    />
                </div>


                {offerLevelDetailsData?.levelType?.id == 4 ? (
                    <div className={shake.percentage ? "shake-error" : ""}>
                        <label>Percentage (%)</label>
                        <input
                            className={`input-scheme ${errors.percentage ? "error-input" : ""}`}
                            placeholder="Enter Percentage"
                            value={selection.percentage}
                            onChange={(e) => handleChange("percentage", e.target.value)}
                        />
                        {errors.percentage && (
                            <span className="error">{errors.percentage}</span>
                        )}
                    </div>

                ) : (
                    <div className={shake.qty ? "shake-error" : ""}>
                        <label>Quantity</label>
                        <input
                            className={`input-scheme ${errors.qty ? "error-input" : ""}`}
                            placeholder="Enter Qty"
                            value={selection.qty}
                            onChange={(e) => handleChange("qty", e.target.value)}
                        />
                        {errors.qty && (
                            <span className="error">{errors.qty}</span>
                        )}
                    </div>
                )}

                <div style={{ display: "flex", alignItems: "end" }}>
                    <button className="sub-button" onClick={handleAdd}>
                        <AddIcon fontSize="small" /> Add
                    </button>
                </div>
            </div>

            {/* ---------- TABLE ---------- */}
            {rows.length > 0 && (
                <div className="table-wrapper mt-18">
                    <table className="scheme-table">
                        <thead>
                            <tr>
                                <th>Sr.No</th>
                                <th>Product</th>
                                <th>Quantity</th>
                                <th>Percentage</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, index) => (
                                <tr key={row.id}>
                                    <td>{index + 1}</td>
                                    <td>{row.product}</td>
                                    <td>{row.qty}</td>
                                    <td>{row.percentage}</td>
                                    <td>
                                        <IconButton onClick={() => rowDelete(row)}>
                                            <DeleteOutlineIcon color="error" />
                                        </IconButton>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
});

const mapStateToProps = (state) => ({
    productList: state.product,
});

const mapDispatchToProps = {
    showLoador,
};

export default connect(mapStateToProps, mapDispatchToProps, null, {
    forwardRef: true,
})(SchemeProductList);

